import {
  MediaRecorder as ExtendableMediaRecorder,
  type IMediaRecorder,
} from 'extendable-media-recorder';
import { convertVideo } from './ffmpeg';

export type StatusMessages =
  | 'media_aborted'
  | 'permission_denied'
  | 'no_specified_media_found'
  | 'media_in_use'
  | 'invalid_media_constraints'
  | 'no_constraints'
  | 'recorder_error'
  | 'idle'
  | 'acquiring_media'
  | 'delayed_start'
  | 'recording'
  | 'stopping'
  | 'stopped'
  | 'paused'
  | 'loading';

export enum RecorderErrors {
  AbortError = 'media_aborted',
  NotAllowedError = 'permission_denied',
  NotFoundError = 'no_specified_media_found',
  NotReadableError = 'media_in_use',
  OverconstrainedError = 'invalid_media_constraints',
  TypeError = 'no_constraints',
  NONE = '',
  NO_RECORDER = 'recorder_error',
  ConversionError = 'conversion_error',
}

export type RecorderStartedOutput = {
  mediaRecorder: IMediaRecorder | null;
  status: StatusMessages;
};

export type RecorderFileReadyOutput = {
  mediaBlob: Blob;
  mediaBlobUrl: string;
  status: StatusMessages;
};

export interface RecorderFileReady {
  onFileReady: ({ mediaBlob, mediaBlobUrl }: RecorderFileReadyOutput) => void;
  onProgress: (progress: number) => void;
  micDeviceId?: string;
}

export type MediaRecorderOptions = {
  audio?: boolean;
  video?: boolean;
  screen?: boolean;
};

export const createMediaRecorder = ({
  audio,
  video,
  screen,
}: MediaRecorderOptions) => {
  let mediaRecorder: IMediaRecorder;
  let status: StatusMessages = 'idle';
  let error = RecorderErrors.NONE;
  let micStream: MediaStream;
  let screenStream: MediaStream;
  let recordingStartTime = 0;
  let recordingDuration = 0;
  let currentProgress = 0;

  async function getCombinedMediaStream(
    micDeviceId: string | undefined,
  ): Promise<MediaStream | undefined> {
    try {
      const displayMediaOptions: any = {
        video: {
          cursor: 'always',
          width: { ideal: 1080 },
          height: { ideal: 720 },
          frameRate: { ideal: 15 },
        },
        audio: true,
      };

      const audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();
      let hasAudio = false;

      screenStream =
        await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);

      const audioConstraint = micDeviceId
        ? { deviceId: { exact: micDeviceId } }
        : true;

      micStream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraint,
      });

      if (micStream.getAudioTracks().length) {
        const micGainNode = audioContext.createMediaStreamSource(micStream);
        micGainNode.connect(destination);
        hasAudio = true;
      } else {
        console.warn('No microphone audio track detected.');
      }
      if (screenStream.getAudioTracks().length) {
        const systemAudioSource =
          audioContext.createMediaStreamSource(screenStream);
        systemAudioSource.connect(destination);
        hasAudio = true;
      } else {
        console.warn('No system audio track detected in screen stream.');
      }

      if (!hasAudio) {
        error = RecorderErrors.NotFoundError;
      }

      const combinedStream = new MediaStream([
        ...screenStream.getVideoTracks(),
        ...destination.stream.getAudioTracks(),
      ]);

      screenStream.getVideoTracks()[0].onended = () => {
        audioContext.close();
        combinedStream.getTracks().forEach((track) => track.stop());
      };

      return combinedStream;
    } catch (err) {
      console.error('Error in getCombinedMediaStream:', err);
      error = RecorderErrors.NO_RECORDER;
    }
  }

  const startRecording = async ({
    onFileReady,
    onProgress,
    micDeviceId,
  }: RecorderFileReady): Promise<RecorderStartedOutput> => {
    error = RecorderErrors.NONE;
    const mediaStream = await getCombinedMediaStream(micDeviceId);

    if (!mediaStream?.active) return { mediaRecorder: null, status };

    mediaRecorder = new ExtendableMediaRecorder(mediaStream);
    const mediaChunks: Blob[] = [];

    const onRecordingActive = ({ data }: BlobEvent) => {
      mediaChunks.push(data);
    };

    const handleProgress = (progress: number) => {
      const raw = Math.round((progress * 100) / recordingDuration);
      currentProgress = Math.min(Math.max(raw, 0), 100);
      onProgress(currentProgress);
    };

    const stopStream = () => {
      // Stop all active tracks, including screen sharing and audio
      if (mediaStream) {
        const tracks = mediaStream.getTracks();
        tracks.forEach((track) => {
          track.stop();
        });
      }
      if (micStream) micStream.getTracks()[0].stop();
      if (screenStream) screenStream.getTracks()[0].stop();
      recordingDuration = Date.now() - recordingStartTime;
    };

    const onRecordingStop = async () => {
      stopStream();

      const fileName = 'screen-record.mp4';
      const fileType = 'video/mp4';
      const mediaBlob = new File(mediaChunks, fileName, { type: fileType });

      try {
        const convertedFile = await convertVideo(
          mediaBlob,
          'output.mp4',
          handleProgress,
        );
        const mediaBlobUrl = URL.createObjectURL(convertedFile);
        onFileReady({ mediaBlob: convertedFile, mediaBlobUrl, status });
      } catch (err) {
        console.error('Error in convertVideo:', err);
        error = RecorderErrors.ConversionError;
      } finally {
        status = 'idle';
      }
    };

    // get Start Time
    recordingStartTime = Date.now();

    mediaRecorder.ondataavailable = onRecordingActive;
    mediaRecorder.onstop = onRecordingStop;
    mediaRecorder.onerror = () => {
      error = RecorderErrors.NO_RECORDER;
      status = 'idle';
    };
    mediaRecorder.start();
    status = 'recording';

    return { mediaRecorder, status };
  };

  const stopRecording = () => {
    mediaRecorder.stop();
  };

  return {
    status,
    startRecording,
    stopRecording,
  };
};
